// ===== Packages =====

import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
}                                               from 'react';
import { ReactSVG }                             from 'react-svg';
import {
    useDrop,
}                                               from 'react-dnd';
import { v4 as uuidv4 }                         from 'uuid';

// ===== Components =====

import { Transition }                           from '../Transition';
import ReaderOutlineChapter                     from '../ReaderOutlineChapter';
import {
    Button,
}                                               from '../Editor/helpers';
import Tooltip                                  from '../Tooltip';

// ===== Enums =====

import {
    DRAGGABLE_COMPONENT_TYPE,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    BUTTON_TYPE,
    TOOLTIP_TYPE,
    USER_ACTION_TYPE,
    POST_OUTLINE_MUTATION_TYPE,
}                                               from '../../enums';
import {
    ZOOM_LEVEL,
}                                               from '../Editor/elements/enums';

// ===== Services =====

import {
    playAudio,
    formatNumber,
    recordUserAction,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    parseSpecialNumbers,
    isMobile,
    isTestMode,
    hasReaderOutlineChapterAccess,
    hasReaderOutlineSectionAccess,
    buildReaderContentsTableItemHref,
}                                               from '../../services';
import useLazyAudioRef                                  from '../../services/useLazyAudioRef';

// ===== Interfaces =====

import {
    IPartialUserItem,
    IDimension,
    IReadPostItem,
    IReadPostChapterItem,
    IContentsChapterItem,
    IPostPathEvent,
    ISnackbarItem,
    IPostPathExpandChapterItem,
    IZoomLevelItem,
    IPostAccessItem,
    IContentsChapterSectionItem,
    IOutlineMutationUpdates,
}                                               from '../../interfaces';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    DEFAULT_AUDIO_VOLUME,
    FADE_IN_DEFAULT_STYLE,
    FADE_IN_TRANSITION_STYLES,
    MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
    FADE_IN_STAGGER_OFFSET_DURATION,
    FADE_IN_STAGGER_TRANSITION_DURATION,
    FADE_IN_STAGGER_DEFAULT_STYLE,
    FADE_IN_STAGGER_TRANSITION_STYLES,
    HOVER_TARGET_CLASSNAME,
    POST_BANNER_LEFT_CONTAINER_WIDTH,
    POST_TITLE_ENTER_DURATION,
    POST_SUBTITLE_ENTER_DURATION,
    POST_THUMBNAIL_ENTER_DURATION,
    POST_METADATA_ENTER_DURATION,
    CONTENTS_TABLE_MARGIN,
    CONTENTS_TABLE_WIDTH_CONTRACTED,
    POST_BANNER_HEIGHT,
    CONTENTS_TABLE_TOP_EXPANDED,
    CONTENTS_TABLE_TOGGLE_BUTTON_CONTAINER_Y_OFFSET_SMALL_VIEWPORT,
    SERIALIZED_TRUE_INITIAL_EDITOR_VALUE,
}                                               from '../../constants/generalConstants';
import MEDIA_QUERY_SIZE                         from '../../constants/mediaQuerySizes';
import CURSOR_SIGN                              from '../../constants/cursorSigns';

// ===== Sounds =====

import SwooshIn                                 from '../../sounds/swoosh_in.mp3';
import SwooshOut                                from '../../sounds/swoosh_out.mp3';

// ===== Images =====

import PlusIcon                                 from '../../images/editor/plus.svg';
import ChevronIcon                              from '../../images/editor/chevron.svg';
import HighlightIcon                            from '../../images/editor/highlight.svg';
import LockIcon                                 from '../../images/lock.svg';
import UnlockIcon                               from '../../images/unlock.svg';

// ===== Styles =====

import {
    ContentsTable,
    ContentsTableTitle,
    ContentsTableChaptersContainer,
    ContentsTableChapter,
    ContentsTableChapterHead,
    ContentsTableChapterTitle,
    ContentsTableChapterAnnotationContainer,
    ContentsTableChapterAnnotationIcon,
    ContentsTableChapterAnnotationText,
    ContentsTableChapterBody,
    ContentsTableChapterDescription,
    ContentsTableChapterSection,
    ContentsTableChapterSectionText,
    ContentsTableChapterSectionLine,
    ContentsTableChapterSectionAnnotationContainer,
    ContentsTableChapterSectionAnnotationIcon,
    ContentsTableChapterSectionAnnotationText,
    ContentsTableProgressBarContainer,
    ContentsTableProgressBar,
    ContentsTableChapterTitleContainer,
    ContentsTableAccordionIcon,
    ContentsTableToggleButtonContainer,
    ContentsTableOutlineContainer,
    AddChapterButton,
    AddChapterButtonIcon,
    ContentsTableLockIcon,
}                                               from './styles';
import { theme as themeObj }                    from '../../themes/theme-context';

ReaderContentsTableOutliner.defaultProps = {
    hide: false,
    onOutlineChaptersChange: undefined,
};
interface Props {
    customCursorActive: boolean,
    visible: boolean,
    hide?: boolean,
    hasSound: boolean,
    user: IPartialUserItem | null,
    userIsAdmin: boolean,
    userPostAccess: Map<string, IPostAccessItem> | undefined,
    currentSessionId: string | null,
    viewportDimensions: IDimension,
    write: boolean,
    post: IReadPostItem | undefined,
    currentZoomLevel: IZoomLevelItem | null,
    contentsTable: IContentsChapterItem[] | null,
    selectedPostValuePath: number[],
    pageLoadRendersCompleted: boolean,
    refreshPost: boolean,
    hasError: boolean,
    hitDetailMinimum: boolean,
    isSteering: boolean,
    notifyUserToSignUpAfterAnnotationCreationAttempt: boolean,
    interactionDisabled: boolean,
    setContentsTableVisible: React.Dispatch<React.SetStateAction<boolean>>,
    handleUpdatePostOutline: (updates: {
        test: boolean;
        postId: string;
    } & IOutlineMutationUpdates) => Promise<boolean | void>,
    onChangeSelectedPostValuePath: (event: IPostPathEvent) => void,
    onSetExpandContentsTableChapter: (
        expandItems: IPostPathExpandChapterItem[],
        e?: React.MouseEvent | React.TouchEvent,
    ) => void,
    setCursorSigns: React.Dispatch<React.SetStateAction<string[]>>,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    contentsTableRef: React.MutableRefObject<HTMLDivElement | null>,
    contentsTableProgressBarContainerRef: React.MutableRefObject<HTMLDivElement | null>,
    contentsTableProgressBarRef: React.MutableRefObject<HTMLDivElement | null>,
    contentsTableToggleButtonContainerRef: React.MutableRefObject<HTMLDivElement | null>,
    refreshContentsTable: (async?: boolean) => Promise<void>,
    onOutlineChaptersChange?: (chapters: IReadPostChapterItem[] | null) => void,
}
function ReaderContentsTableOutliner({
    customCursorActive,
    visible,
    hide,
    hasSound,
    user,
    userIsAdmin,
    userPostAccess,
    currentSessionId,
    viewportDimensions,
    write,
    post,
    currentZoomLevel,
    contentsTable,
    selectedPostValuePath,
    pageLoadRendersCompleted,
    refreshPost,
    hasError,
    hitDetailMinimum,
    isSteering,
    notifyUserToSignUpAfterAnnotationCreationAttempt,
    interactionDisabled,
    setContentsTableVisible,
    handleUpdatePostOutline,
    onChangeSelectedPostValuePath,
    onSetExpandContentsTableChapter,
    setCursorSigns,
    onCursorEnter,
    onCursorLeave,
    setSnackbarData,
    contentsTableRef,
    contentsTableProgressBarContainerRef,
    contentsTableProgressBarRef,
    contentsTableToggleButtonContainerRef,
    refreshContentsTable,
    onOutlineChaptersChange,
}: Props): JSX.Element {
    // ===== General Constants =====

    const DEFAULT_LINE_HEIGHT = 0.075 * BODY_FONT_SIZE;
    const CONTENTS_TABLE_MARGIN_RIGHT = 1.875 * BODY_FONT_SIZE;
    const CONTENTS_TABLE_WIDTH_EXPANDED = POST_BANNER_LEFT_CONTAINER_WIDTH - CONTENTS_TABLE_MARGIN_RIGHT;
    const CONTENTS_TABLE_TOGGLE_BUTTON_LENGTH = 1.5625 * BODY_FONT_SIZE;
    const CONTENTS_TABLE_TOGGLE_BUTTON_MARGIN = 0.3125 * BODY_FONT_SIZE;
    const CONTENTS_TABLE_TITLE_FONT_MULTIPLIER = 0.85;
    const CONTENTS_TABLE_TITLE_PADDING_TOP = 0.625 * BODY_FONT_SIZE;
    // const CONTENTS_TABLE_TITLE_HEIGHT = DEFAULT_LINE_HEIGHT * CONTENTS_TABLE_TITLE_FONT_MULTIPLIER * BODY_FONT_SIZE
    //     + CONTENTS_TABLE_TITLE_PADDING_TOP;
    const CHAPTER_HEAD_TITLE_FONT_MULTIPLIER = 1;
    const CHAPTER_HEAD_DESCRIPTION_FONT_MULTIPLIER = 0.85;
    const CHAPTER_SECTION_FONT_MULTIPLIER = 0.95;
    const CHAPTER_SECTION_PADDING = 0.3125 * BODY_FONT_SIZE;
    const CHAPTER_SECTION_HEIGHT = DEFAULT_LINE_HEIGHT * CHAPTER_SECTION_FONT_MULTIPLIER * BODY_FONT_SIZE
        + 2 * CHAPTER_SECTION_PADDING;
    const CHAPTER_ANNOTATION_TEXT_FONT_MULTIPLIER = CHAPTER_HEAD_DESCRIPTION_FONT_MULTIPLIER;
    const CHAPTER_HEAD_PADDING = 0.625 * BODY_FONT_SIZE;
    // const CHAPTER_HEAD_HEIGHT = DEFAULT_LINE_HEIGHT * CHAPTER_HEAD_TITLE_FONT_MULTIPLIER * BODY_FONT_SIZE
    //     + DEFAULT_LINE_HEIGHT * CHAPTER_HEAD_DESCRIPTION_FONT_MULTIPLIER * BODY_FONT_SIZE
    //     + 2 * CHAPTER_HEAD_PADDING;
    const CHAPTER_HEIGHT_BUFFER = 1.25 * BODY_FONT_SIZE;
    const OUTLINE_METADATA_UPDATE_DEBOUNCE_DURATION = 500;

    // ===== Refs =====

    // ----- Sound Clips
    const swooshInClip = useLazyAudioRef({
        src: SwooshIn,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const swooshOutClip = useLazyAudioRef({
        src: SwooshOut,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const outlineChaptersRef = useRef<IReadPostChapterItem[]>(post?.chapters || []);
    const optimisticOutlineOrderKeyRef = useRef<string | null>(null);
    const latestSelectedPostValuePathRef = useRef<number[]>(selectedPostValuePath);
    const pendingOutlineRefreshActionRef = useRef<POST_OUTLINE_MUTATION_TYPE | null>(null);
    const pendingChapterSelectionRestorePathRef = useRef<number[] | null>(null);
    const pendingSectionSelectionRestorePathRef = useRef<number[] | null>(null);
    const pendingOutlineMetadataUpdateTimeoutsRef = useRef<Record<string, number>>({});
    const pendingOutlineMetadataUpdateValuesRef = useRef<Record<string, string>>({});
    const ignoreContentsTableClickUntilTimestampRef = useRef<number>(0);

    // ===== State =====

    // Set up drop target for outline chapters
    const [, drop] = useDrop(() => ({ accept: DRAGGABLE_COMPONENT_TYPE.editorOutlineTitle }));
    // Indicates whether user is hovering over contents table toggle button
    const [isContentsTableToggleButtonMouseOver, setIsContentsTableToggleButtonMouseOver] = useState<boolean>(false);
    // Indicates whether we're updating the contents table
    const [updatingContentsTable, setUpdatingContentsTable] = useState<boolean>(false);
    const [outlineChapters, setOutlineChapters] = useState<IReadPostChapterItem[]>(post?.chapters || []);

    const annotationCountsByChapterId = useMemo(() => (
        (contentsTable || []).reduce((acc, chapter) => {
            acc[chapter.id] = chapter.annotationCount;
            return acc;
        }, {} as Record<string, number>)
    ), [contentsTable]);

    const annotationCountsBySectionId = useMemo(() => (
        (contentsTable || []).reduce((acc, chapter) => {
            chapter.sections?.forEach((section) => {
                acc[section.id] = section.annotationCount;
            });
            return acc;
        }, {} as Record<string, number>)
    ), [contentsTable]);
    const getOutlineOrderKey = useCallback((chapters: IReadPostChapterItem[]): string => (
        (chapters || []).map((chapter) => (
            `${chapter.id}:${(chapter.sections || []).map((section) => section.id).join(',')}`
        )).join('::')
    ), []);

    const outlineChapterOrderKey = useMemo(() => (
        getOutlineOrderKey(outlineChapters)
    ), [getOutlineOrderKey, outlineChapters]);

    useEffect(() => {
        outlineChaptersRef.current = outlineChapters;
    }, [outlineChapters]);

    useEffect(() => {
        if (!write || !onOutlineChaptersChange) {
            return undefined;
        }

        onOutlineChaptersChange(outlineChapters);
        return undefined;
    }, [
        onOutlineChaptersChange,
        outlineChapters,
        write,
    ]);

    useEffect(() => () => {
        if (onOutlineChaptersChange) {
            onOutlineChaptersChange(null);
        }
    }, [onOutlineChaptersChange]);

    useEffect(() => {
        latestSelectedPostValuePathRef.current = selectedPostValuePath;
    }, [selectedPostValuePath]);

    useEffect(() => () => {
        Object.values(pendingOutlineMetadataUpdateTimeoutsRef.current).forEach((timeoutId) => {
            window.clearTimeout(timeoutId);
        });
        pendingOutlineMetadataUpdateTimeoutsRef.current = {};
    }, []);

    const getReindexedOutlineChapters = useCallback((chapters: IReadPostChapterItem[]): IReadPostChapterItem[] => (
        (chapters || []).map((chapter, index) => ({
            ...chapter,
            index,
        }))
    ), []);

    const reorderOutlineChapters = useCallback((
        chapters: IReadPostChapterItem[],
        draggedChapterId: string,
        droppedIndex: number,
    ): IReadPostChapterItem[] => {
        const currentChapters = chapters || [];
        const draggedIndex = currentChapters.findIndex((chapter) => chapter.id === draggedChapterId);

        if (
            draggedIndex === -1
            || draggedIndex === droppedIndex
            || droppedIndex < 0
            || droppedIndex >= currentChapters.length
        ) {
            return currentChapters;
        }

        const nextChapters = [...currentChapters];
        const [draggedChapter] = nextChapters.splice(draggedIndex, 1);
        nextChapters.splice(droppedIndex, 0, draggedChapter);
        return getReindexedOutlineChapters(nextChapters);
    }, [getReindexedOutlineChapters]);

    const reorderOutlineSections = useCallback((
        chapters: IReadPostChapterItem[],
        chapterId: string,
        draggedSectionId: string,
        droppedIndex: number,
    ): IReadPostChapterItem[] => {
        const nextChapters = getReindexedOutlineChapters(chapters || []);
        const targetChapterIndex = nextChapters.findIndex((chapter) => chapter.id === chapterId);

        if (targetChapterIndex < 0) {
            return nextChapters;
        }

        const targetChapter = nextChapters[targetChapterIndex];
        const currentSections = targetChapter.sections || [];
        const draggedIndex = currentSections.findIndex((section) => section.id === draggedSectionId);

        if (
            draggedIndex === -1
            || draggedIndex === droppedIndex
            || droppedIndex < 0
            || droppedIndex >= currentSections.length
        ) {
            return nextChapters;
        }

        const nextSections = [...currentSections];
        const [draggedSection] = nextSections.splice(draggedIndex, 1);
        nextSections.splice(droppedIndex, 0, draggedSection);

        nextChapters[targetChapterIndex] = {
            ...targetChapter,
            sections: nextSections.map((section, index) => ({
                ...section,
                index,
            })),
        };

        return nextChapters;
    }, [getReindexedOutlineChapters]);

    const applyOptimisticOutlineChapters = useCallback((nextChapters: IReadPostChapterItem[]): void => {
        optimisticOutlineOrderKeyRef.current = getOutlineOrderKey(nextChapters);
        outlineChaptersRef.current = nextChapters;
        setOutlineChapters(nextChapters);
        if (write && onOutlineChaptersChange) {
            onOutlineChaptersChange(nextChapters);
        }
    }, [getOutlineOrderKey, onOutlineChaptersChange, write]);

    const rollbackOptimisticOutlineChapters = useCallback((originalChapters: IReadPostChapterItem[]): void => {
        optimisticOutlineOrderKeyRef.current = null;
        outlineChaptersRef.current = originalChapters;
        setOutlineChapters(originalChapters);
        if (write && onOutlineChaptersChange) {
            onOutlineChaptersChange(originalChapters);
        }
    }, [onOutlineChaptersChange, write]);

    const getSelectionAfterChapterDelete = useCallback((
        nextChapters: IReadPostChapterItem[],
        deletedChapterIndex: number,
        path: number[],
    ): number[] => {
        if (nextChapters.length === 0) {
            return [];
        }

        if (path.length === 0) {
            return [];
        }

        if (path[0] < deletedChapterIndex) {
            return path;
        }

        if (path[0] > deletedChapterIndex) {
            return [path[0] - 1, ...path.slice(1)];
        }

        const replacementChapterIndex = Math.min(deletedChapterIndex, nextChapters.length - 1);
        const replacementChapter = nextChapters[replacementChapterIndex];

        if ((replacementChapter.sections || []).length > 0) {
            return [replacementChapterIndex, replacementChapter.sections!.length - 1];
        }

        return [replacementChapterIndex];
    }, []);

    const getSelectionAfterSectionDelete = useCallback((
        nextChapters: IReadPostChapterItem[],
        chapterIndex: number,
        deletedSectionIndex: number,
        path: number[],
    ): number[] => {
        if (path.length < 2 || path[0] !== chapterIndex) {
            return path;
        }

        const nextSections = nextChapters[chapterIndex]?.sections || [];
        if (path[1] < deletedSectionIndex) {
            return path;
        }

        if (path[1] > deletedSectionIndex) {
            return [chapterIndex, path[1] - 1];
        }

        if (nextSections.length === 0) {
            return [chapterIndex];
        }

        return [chapterIndex, Math.max(0, deletedSectionIndex - 1)];
    }, []);

    const getSelectedPathForReorderedOutline = useCallback((
        currentChapters: IReadPostChapterItem[],
        nextChapters: IReadPostChapterItem[],
        path: number[],
    ): number[] | null => {
        if (path.length === 0) {
            return null;
        }

        const selectedChapter = currentChapters[path[0]];
        if (!selectedChapter) {
            return null;
        }

        const nextChapterIndex = nextChapters.findIndex((chapter) => chapter.id === selectedChapter.id);
        if (nextChapterIndex < 0) {
            return null;
        }

        if (path.length === 1) {
            return [nextChapterIndex];
        }

        const selectedSection = selectedChapter.sections?.[path[1]];
        if (!selectedSection) {
            return [nextChapterIndex];
        }

        const nextSectionIndex = (nextChapters[nextChapterIndex].sections || [])
            .findIndex((section) => section.id === selectedSection.id);

        if (nextSectionIndex < 0) {
            return [nextChapterIndex];
        }

        return [nextChapterIndex, nextSectionIndex];
    }, []);

    // ===== Animation Constants =====

    const CONTENTS_TABLE_TRANSITION_DURATION = 200;
    const CONTENTS_TABLE_PROGRESS_BAR_TRANSITION_DURATION = 200;
    const CONTENTS_TABLE_CHAPTER_ENTER_DURATION = 200;
    const CONTENTS_TABLE_CHAPTER_TRANSITION_DURATION = 200;
    const CONTENTS_TABLE_CHAPTER_SECTION_TRANSITION_DURATION = 50;
    const CONTENTS_TABLE_CHAPTER_SECTION_TEXT_TRANSITION_DURATION = 200;
    const CONTENTS_TABLE_ENTER_TRANSITION_DURATION = 100;
    // Duration (in milliseconds) for add chapter button to respond to hover over
    const ADD_CHAPTER_BUTTON_TRANSITION_DURATION = 150;

    // ===== Methods =====

    /**
     * Updates chapter title in post
     * @param chapterIndex index of chapter title to update
     * @param title new value of title
     */
    const persistChapterTitle = useCallback((chapterId: string, title: string): void => {
        if (!post) return;
        handleUpdatePostOutline({
            test: isTestMode(),
            postId: post.id,
            action: POST_OUTLINE_MUTATION_TYPE.renameChapter,
            chapterId,
            title,
        });
    }, [handleUpdatePostOutline, post]);

    /**
     * Updates chapter description in post
     * @param chapterIndex index of chapter description to update
     * @param description new value of description
     */
    const persistChapterDescription = useCallback((chapterId: string, description: string): void => {
        if (!post) return;
        handleUpdatePostOutline({
            test: isTestMode(),
            postId: post.id,
            action: POST_OUTLINE_MUTATION_TYPE.updateChapterDescription,
            chapterId,
            description,
        });
    }, [handleUpdatePostOutline, post]);

    /**
     * Updates chapter title in post
     * @param chapterIndex index of section's chapter
     * @param sectionIndex index of section title to update
     * @param title new value of title
     */
    const persistSectionTitle = useCallback((chapterId: string, sectionId: string, title: string): void => {
        if (!post) return;
        handleUpdatePostOutline({
            test: isTestMode(),
            postId: post.id,
            action: POST_OUTLINE_MUTATION_TYPE.renameSection,
            chapterId,
            sectionId,
            title,
        });
    }, [handleUpdatePostOutline, post]);

    const queueOutlineMetadataUpdate = useCallback((
        key: string,
        value: string,
        persist: () => void,
    ): void => {
        pendingOutlineMetadataUpdateValuesRef.current[key] = value;

        if (pendingOutlineMetadataUpdateTimeoutsRef.current[key] != null) {
            window.clearTimeout(pendingOutlineMetadataUpdateTimeoutsRef.current[key]);
        }

        pendingOutlineMetadataUpdateTimeoutsRef.current[key] = window.setTimeout(() => {
            persist();
            delete pendingOutlineMetadataUpdateTimeoutsRef.current[key];
        }, OUTLINE_METADATA_UPDATE_DEBOUNCE_DURATION);
    }, []);

    const flushOutlineMetadataUpdate = useCallback((
        key: string,
        value: string,
        persist: () => void,
    ): void => {
        pendingOutlineMetadataUpdateValuesRef.current[key] = value;

        if (pendingOutlineMetadataUpdateTimeoutsRef.current[key] != null) {
            window.clearTimeout(pendingOutlineMetadataUpdateTimeoutsRef.current[key]);
            delete pendingOutlineMetadataUpdateTimeoutsRef.current[key];
        }

        persist();
    }, []);

    const updateChapterTitle = useCallback((chapterId: string, title: string): void => {
        const key = `chapter-title:${chapterId}`;
        queueOutlineMetadataUpdate(
            key,
            title,
            () => persistChapterTitle(chapterId, pendingOutlineMetadataUpdateValuesRef.current[key]),
        );
    }, [persistChapterTitle, queueOutlineMetadataUpdate]);

    const flushChapterTitle = useCallback((chapterId: string, title: string): void => {
        const key = `chapter-title:${chapterId}`;
        flushOutlineMetadataUpdate(
            key,
            title,
            () => persistChapterTitle(chapterId, pendingOutlineMetadataUpdateValuesRef.current[key]),
        );
    }, [flushOutlineMetadataUpdate, persistChapterTitle]);

    const updateChapterDescription = useCallback((chapterId: string, description: string): void => {
        const key = `chapter-description:${chapterId}`;
        queueOutlineMetadataUpdate(
            key,
            description,
            () => persistChapterDescription(chapterId, pendingOutlineMetadataUpdateValuesRef.current[key]),
        );
    }, [persistChapterDescription, queueOutlineMetadataUpdate]);

    const flushChapterDescription = useCallback((chapterId: string, description: string): void => {
        const key = `chapter-description:${chapterId}`;
        flushOutlineMetadataUpdate(
            key,
            description,
            () => persistChapterDescription(chapterId, pendingOutlineMetadataUpdateValuesRef.current[key]),
        );
    }, [flushOutlineMetadataUpdate, persistChapterDescription]);

    const updateSectionTitle = useCallback((chapterId: string, sectionId: string, title: string): void => {
        const key = `section-title:${sectionId}`;
        queueOutlineMetadataUpdate(
            key,
            title,
            () => persistSectionTitle(chapterId, sectionId, pendingOutlineMetadataUpdateValuesRef.current[key]),
        );
    }, [persistSectionTitle, queueOutlineMetadataUpdate]);

    const flushSectionTitle = useCallback((chapterId: string, sectionId: string, title: string): void => {
        const key = `section-title:${sectionId}`;
        flushOutlineMetadataUpdate(
            key,
            title,
            () => persistSectionTitle(chapterId, sectionId, pendingOutlineMetadataUpdateValuesRef.current[key]),
        );
    }, [flushOutlineMetadataUpdate, persistSectionTitle]);

    const executeOutlineMutation = async (updates: IOutlineMutationUpdates): Promise<void> => {
        if (!post) return;
        const shouldRefreshContentsTable = (
            updates.action === POST_OUTLINE_MUTATION_TYPE.createChapter
            || updates.action === POST_OUTLINE_MUTATION_TYPE.moveChapter
            || updates.action === POST_OUTLINE_MUTATION_TYPE.deleteChapter
            || updates.action === POST_OUTLINE_MUTATION_TYPE.createSection
            || updates.action === POST_OUTLINE_MUTATION_TYPE.moveSection
            || updates.action === POST_OUTLINE_MUTATION_TYPE.deleteSection
        );
        if (shouldRefreshContentsTable && !updatingContentsTable) {
            setUpdatingContentsTable(true);
        }
        if (shouldRefreshContentsTable) {
            pendingOutlineRefreshActionRef.current = updates.action;
        }
        await handleUpdatePostOutline({
            test: isTestMode(),
            postId: post.id,
            ...updates,
        });
        if (shouldRefreshContentsTable) {
            await refreshContentsTable(true);
            setUpdatingContentsTable(false);
        }
    };

    const previewMoveChapter = useCallback((draggedChapterId: string, droppedIndex: number): void => {
        let nextSelectedPath: number[] | null = null;
        setOutlineChapters((currentChapters) => {
            if (pendingChapterSelectionRestorePathRef.current == null) {
                pendingChapterSelectionRestorePathRef.current = [...latestSelectedPostValuePathRef.current];
            }
            const nextChapters = reorderOutlineChapters(currentChapters, draggedChapterId, droppedIndex);
            nextSelectedPath = getSelectedPathForReorderedOutline(
                currentChapters,
                nextChapters,
                latestSelectedPostValuePathRef.current,
            );
            optimisticOutlineOrderKeyRef.current = getOutlineOrderKey(nextChapters);
            outlineChaptersRef.current = nextChapters;
            return nextChapters;
        });
        const resolvedNextSelectedPath = nextSelectedPath as number[] | null;
        if (
            resolvedNextSelectedPath != null
            && (
                resolvedNextSelectedPath.length !== latestSelectedPostValuePathRef.current.length
                || resolvedNextSelectedPath.some((value, index) => value !== latestSelectedPostValuePathRef.current[index])
            )
        ) {
            onChangeSelectedPostValuePath({
                path: resolvedNextSelectedPath,
            });
        }
    }, [getOutlineOrderKey, getSelectedPathForReorderedOutline, onChangeSelectedPostValuePath, reorderOutlineChapters]);

    const resetMoveChapterPreview = useCallback((): void => {
        optimisticOutlineOrderKeyRef.current = null;
        const nextChapters = getReindexedOutlineChapters(post?.chapters || []);
        outlineChaptersRef.current = nextChapters;
        setOutlineChapters(nextChapters);
        if (pendingChapterSelectionRestorePathRef.current != null) {
            onChangeSelectedPostValuePath({
                path: pendingChapterSelectionRestorePathRef.current,
            });
            pendingChapterSelectionRestorePathRef.current = null;
        }
    }, [getReindexedOutlineChapters, onChangeSelectedPostValuePath, post?.chapters]);

    const commitMoveChapter = useCallback(async (
        draggedChapterId: string,
        droppedIndex: number,
        originalIndex: number,
    ): Promise<void> => {
        if (!post) return;

        const originalChapters = getReindexedOutlineChapters(post.chapters || []);
        const resolvedOriginalIndex = originalChapters.findIndex((chapter) => chapter.id === draggedChapterId);
        const stableOriginalIndex = resolvedOriginalIndex >= 0 ? resolvedOriginalIndex : originalIndex;

        if (stableOriginalIndex === droppedIndex) {
            optimisticOutlineOrderKeyRef.current = null;
            pendingChapterSelectionRestorePathRef.current = null;
            return;
        }

        const nextChapters = reorderOutlineChapters(outlineChaptersRef.current, draggedChapterId, droppedIndex);
        const nextSelectedPath = getSelectedPathForReorderedOutline(
            outlineChaptersRef.current,
            nextChapters,
            latestSelectedPostValuePathRef.current,
        );
        const resolvedNextSelectedPath = nextSelectedPath as number[] | null;
        optimisticOutlineOrderKeyRef.current = getOutlineOrderKey(nextChapters);
        outlineChaptersRef.current = nextChapters;
        setOutlineChapters(nextChapters);
        if (
            resolvedNextSelectedPath != null
            && (
                resolvedNextSelectedPath.length !== latestSelectedPostValuePathRef.current.length
                || resolvedNextSelectedPath.some((value, index) => value !== latestSelectedPostValuePathRef.current[index])
            )
        ) {
            onChangeSelectedPostValuePath({
                path: resolvedNextSelectedPath,
            });
        }
        try {
            await executeOutlineMutation({
                action: POST_OUTLINE_MUTATION_TYPE.moveChapter,
                chapterId: draggedChapterId,
                targetIndex: droppedIndex,
            });

            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.movePostChapter,
                    sessionId: currentSessionId,
                    payload: {
                        postId: post.id,
                        chapterId: draggedChapterId,
                        direction: droppedIndex > stableOriginalIndex ? 'down' : 'up',
                        units: Math.abs(droppedIndex - stableOriginalIndex),
                    },
                });
            }
        } catch (error) {
            optimisticOutlineOrderKeyRef.current = null;
            outlineChaptersRef.current = originalChapters;
            setOutlineChapters(originalChapters);
            if (pendingChapterSelectionRestorePathRef.current != null) {
                onChangeSelectedPostValuePath({
                    path: pendingChapterSelectionRestorePathRef.current,
                });
            }
            pendingChapterSelectionRestorePathRef.current = null;
            throw error;
        }
        pendingChapterSelectionRestorePathRef.current = null;
    }, [
        post,
        currentSessionId,
        executeOutlineMutation,
        getOutlineOrderKey,
        getReindexedOutlineChapters,
        getSelectedPathForReorderedOutline,
        onChangeSelectedPostValuePath,
        reorderOutlineChapters,
    ]);

    const previewMoveSection = useCallback((
        chapterId: string,
        draggedSectionId: string,
        droppedIndex: number,
    ): void => {
        let nextSelectedPath: number[] | null = null;
        setOutlineChapters((currentChapters) => {
            if (pendingSectionSelectionRestorePathRef.current == null) {
                pendingSectionSelectionRestorePathRef.current = [...latestSelectedPostValuePathRef.current];
            }
            const nextChapters = reorderOutlineSections(currentChapters, chapterId, draggedSectionId, droppedIndex);
            nextSelectedPath = getSelectedPathForReorderedOutline(
                currentChapters,
                nextChapters,
                latestSelectedPostValuePathRef.current,
            );
            optimisticOutlineOrderKeyRef.current = getOutlineOrderKey(nextChapters);
            outlineChaptersRef.current = nextChapters;
            return nextChapters;
        });
        const resolvedNextSelectedPath = nextSelectedPath as number[] | null;
        if (
            resolvedNextSelectedPath != null
            && (
                resolvedNextSelectedPath.length !== latestSelectedPostValuePathRef.current.length
                || resolvedNextSelectedPath.some((value, index) => value !== latestSelectedPostValuePathRef.current[index])
            )
        ) {
            onChangeSelectedPostValuePath({
                path: resolvedNextSelectedPath,
            });
        }
    }, [getOutlineOrderKey, getSelectedPathForReorderedOutline, onChangeSelectedPostValuePath, reorderOutlineSections]);

    const resetMoveSectionPreview = useCallback((): void => {
        optimisticOutlineOrderKeyRef.current = null;
        const nextChapters = getReindexedOutlineChapters(post?.chapters || []);
        outlineChaptersRef.current = nextChapters;
        setOutlineChapters(nextChapters);
        if (pendingSectionSelectionRestorePathRef.current != null) {
            onChangeSelectedPostValuePath({
                path: pendingSectionSelectionRestorePathRef.current,
            });
            pendingSectionSelectionRestorePathRef.current = null;
        }
    }, [getReindexedOutlineChapters, onChangeSelectedPostValuePath, post?.chapters]);

    const commitMoveSection = useCallback(async (
        chapterId: string,
        draggedSectionId: string,
        droppedIndex: number,
        originalIndex: number,
    ): Promise<void> => {
        if (!post) return;

        const originalChapters = getReindexedOutlineChapters(post.chapters || []);
        const originalChapter = originalChapters.find((chapter) => chapter.id === chapterId);
        const chapterIndex = originalChapters.findIndex((chapter) => chapter.id === chapterId);
        const resolvedOriginalIndex = (originalChapter?.sections || [])
            .findIndex((section) => section.id === draggedSectionId);
        const stableOriginalIndex = resolvedOriginalIndex >= 0 ? resolvedOriginalIndex : originalIndex;

        if (stableOriginalIndex === droppedIndex) {
            optimisticOutlineOrderKeyRef.current = null;
            pendingSectionSelectionRestorePathRef.current = null;
            return;
        }

        const nextChapters = reorderOutlineSections(
            outlineChaptersRef.current,
            chapterId,
            draggedSectionId,
            droppedIndex,
        );
        const nextSelectedPath = getSelectedPathForReorderedOutline(
            outlineChaptersRef.current,
            nextChapters,
            latestSelectedPostValuePathRef.current,
        );
        optimisticOutlineOrderKeyRef.current = getOutlineOrderKey(nextChapters);
        outlineChaptersRef.current = nextChapters;
        setOutlineChapters(nextChapters);
        if (
            nextSelectedPath != null
            && (
                nextSelectedPath.length !== latestSelectedPostValuePathRef.current.length
                || nextSelectedPath.some((value, index) => value !== latestSelectedPostValuePathRef.current[index])
            )
        ) {
            onChangeSelectedPostValuePath({
                path: nextSelectedPath,
            });
        }
        try {
            await executeOutlineMutation({
                action: POST_OUTLINE_MUTATION_TYPE.moveSection,
                chapterId,
                sectionId: draggedSectionId,
                targetIndex: droppedIndex,
            });

            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.movePostChapterSection,
                    sessionId: currentSessionId,
                    payload: {
                        postId: post.id,
                        chapterId,
                        sectionId: draggedSectionId,
                        direction: droppedIndex > stableOriginalIndex ? 'down' : 'up',
                        units: Math.abs(droppedIndex - stableOriginalIndex),
                    },
                });
            }
        } catch (error) {
            optimisticOutlineOrderKeyRef.current = null;
            outlineChaptersRef.current = originalChapters;
            setOutlineChapters(originalChapters);
            if (pendingSectionSelectionRestorePathRef.current != null) {
                onChangeSelectedPostValuePath({
                    path: pendingSectionSelectionRestorePathRef.current,
                });
            }
            pendingSectionSelectionRestorePathRef.current = null;
            throw error;
        }
        pendingSectionSelectionRestorePathRef.current = null;
    }, [
        post,
        currentSessionId,
        executeOutlineMutation,
        getOutlineOrderKey,
        getReindexedOutlineChapters,
        getSelectedPathForReorderedOutline,
        onChangeSelectedPostValuePath,
        reorderOutlineSections,
    ]);

    /**
     * Manages response to contents table button hovering over
     * @param e mouse or touch event
     */
    const onContentsTableButtonMouseEnter = (e: React.MouseEvent): void => {
        onCursorLeave();
        onCursorEnter(
            CURSOR_TARGET.contentsTableButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    /**
     * Manages response to contents table button no longer hovering over
     */
    const onContentsTableButtonMouseLeave = (e: React.MouseEvent): void => {
        onCursorLeave(e);
    };

    /**
     * Manages response to contents table button hovering over
     * @param e mouse or touch event
     */
    const onContentsTableToggleButtonMouseEnter = (e: React.MouseEvent): void => {
        onContentsTableButtonMouseEnter(e);
        setIsContentsTableToggleButtonMouseOver(true);
    };

    /**
     * Manages response to contents table button no longer hovering over
     */
    const onContentsTableToggleButtonMouseLeave = (e: React.MouseEvent): void => {
        onContentsTableButtonMouseLeave(e);
        setIsContentsTableToggleButtonMouseOver(false);
    };

    /**
     * Manages response to scrolling contents table
     * @param e scroll event
     */
    const onContentsTableScroll = (e: React.UIEvent): void => {
        const target = e.target as HTMLElement;
        // eslint-disable-next-line no-param-reassign
        if (contentsTableProgressBarContainerRef.current) contentsTableProgressBarContainerRef.current.style.top = `${target.scrollTop}px`;
    };

    /**
     * Reveals or hides contents table.
     * Prevents mobile browsers from replaying the touch as a click into newly
     * revealed table rows after the toggle moves out from under the finger.
     * @param e mouse, touch, or pointer event
    */
    const onToggleContentsTable = (e: React.MouseEvent | React.TouchEvent | React.PointerEvent): void => {
        e.preventDefault();
        e.stopPropagation();

        if (!visible) {
            ignoreContentsTableClickUntilTimestampRef.current = Date.now() + 500;
        }

        setContentsTableVisible(!visible);

        // Play Sound
        if (
            !visible
            && hasSound
            && swooshInClip.current
        ) {
            swooshInClip.current.pause();
            swooshInClip.current.currentTime = 0;
            playAudio(swooshInClip.current);
        } else if (
            visible
            && hasSound
            && swooshOutClip.current
        ) {
            swooshOutClip.current.pause();
            swooshOutClip.current.currentTime = 0;
            playAudio(swooshOutClip.current);
        }

        // Record User Action
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.toggleContentsTable,
                sessionId: currentSessionId,
                payload: {
                    expanded: !visible,
                },
            });
        }
    };

    /**
     * Guards against the synthetic click mobile browsers can emit after a touch
     * opens the table, when the first row has moved beneath the user's finger.
     */
    const shouldIgnoreContentsTableClick = (): boolean => (
        Date.now() < ignoreContentsTableClickUntilTimestampRef.current
    );

    /**
     * Adds a new chapter to the contents table
     */
    const onAddChapterButtonMouseDown = (): void => {
        if (!post) return;
        if (!user) return;
        const chapterId = uuidv4();
        const originalChapters = getReindexedOutlineChapters(outlineChaptersRef.current || []);
        const previousSelectedPath = [...latestSelectedPostValuePathRef.current];
        const newChapterIndex = originalChapters.length;
        const shouldMoveRootContentIntoNewChapter = originalChapters.length === 0;
        const optimisticNewChapterValue = shouldMoveRootContentIntoNewChapter
            ? (post.value || SERIALIZED_TRUE_INITIAL_EDITOR_VALUE)
            : SERIALIZED_TRUE_INITIAL_EDITOR_VALUE;
        const nextChapters = getReindexedOutlineChapters([
            ...originalChapters,
            {
                id: chapterId,
                index: newChapterIndex,
                title: '',
                description: '',
                value: optimisticNewChapterValue,
                sections: [],
                published: Date.now(),
            },
        ]);
        applyOptimisticOutlineChapters(nextChapters);
        executeOutlineMutation({
            action: POST_OUTLINE_MUTATION_TYPE.createChapter,
            newChapterId: chapterId,
        }).catch(() => {
            rollbackOptimisticOutlineChapters(originalChapters);
            onChangeSelectedPostValuePath({
                path: previousSelectedPath,
            });
        });

        // Move to new chapter
        onChangeSelectedPostValuePath({
            path: [newChapterIndex],
        });

        // Record User Action
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.addPostChapter,
                sessionId: currentSessionId,
                payload: {
                    postId: post.id,
                    chapterId,
                },
            });
        }
    };

    const addSection = useCallback((chapterId: string): string | null => {
        const originalChapters = getReindexedOutlineChapters(outlineChaptersRef.current || []);
        const previousSelectedPath = [...latestSelectedPostValuePathRef.current];
        const chapterIndex = originalChapters.findIndex((chapter) => chapter.id === chapterId);

        if (chapterIndex < 0) {
            return null;
        }

        const sectionId = uuidv4();
        const targetChapter = originalChapters[chapterIndex];
        // Use the canonical chapter body as the migration source because the
        // outline cache is about structure and can lag the latest body edits.
        const canonicalChapter = (post?.chapters || []).find((chapter) => chapter.id === chapterId);
        const shouldMoveChapterContentIntoNewSection = (targetChapter.sections || []).length === 0;
        const sourceChapterValue = canonicalChapter?.value ?? targetChapter.value;
        const optimisticNewSectionValue = shouldMoveChapterContentIntoNewSection
            ? (sourceChapterValue || SERIALIZED_TRUE_INITIAL_EDITOR_VALUE)
            : SERIALIZED_TRUE_INITIAL_EDITOR_VALUE;
        const nextSections = [
            ...(targetChapter.sections || []),
            {
                id: sectionId,
                index: (targetChapter.sections || []).length,
                title: '',
                value: optimisticNewSectionValue,
                published: Date.now(),
            },
        ];
        const nextChapters = [...originalChapters];
        nextChapters[chapterIndex] = {
            ...targetChapter,
            ...(shouldMoveChapterContentIntoNewSection
                ? { value: SERIALIZED_TRUE_INITIAL_EDITOR_VALUE }
                : {}),
            sections: nextSections,
        };

        applyOptimisticOutlineChapters(getReindexedOutlineChapters(nextChapters));
        executeOutlineMutation({
            action: POST_OUTLINE_MUTATION_TYPE.createSection,
            chapterId,
            newSectionId: sectionId,
        }).catch(() => {
            rollbackOptimisticOutlineChapters(originalChapters);
            onChangeSelectedPostValuePath({
                path: previousSelectedPath,
            });
        });

        return sectionId;
    }, [
        applyOptimisticOutlineChapters,
        executeOutlineMutation,
        getReindexedOutlineChapters,
        post?.chapters,
        rollbackOptimisticOutlineChapters,
    ]);

    const deleteChapter = useCallback((chapterIndex: number): void => {
        const originalChapters = getReindexedOutlineChapters(outlineChaptersRef.current || []);
        const removedChapter = originalChapters[chapterIndex];

        if (!removedChapter) {
            return;
        }

        const previousSelectedPath = [...latestSelectedPostValuePathRef.current];
        const nextChapters = getReindexedOutlineChapters(
            originalChapters.filter((chapter) => chapter.id !== removedChapter.id),
        );
        const nextPath = getSelectionAfterChapterDelete(
            nextChapters,
            chapterIndex,
            previousSelectedPath,
        );

        applyOptimisticOutlineChapters(nextChapters);
        onChangeSelectedPostValuePath({
            path: nextPath,
            // Force a clean editor remount when the selected scope is deleted.
            clearEditorSelection: true,
        });

        executeOutlineMutation({
            action: POST_OUTLINE_MUTATION_TYPE.deleteChapter,
            chapterId: removedChapter.id,
        }).catch(() => {
            rollbackOptimisticOutlineChapters(originalChapters);
            onChangeSelectedPostValuePath({
                path: previousSelectedPath,
            });
        });
    }, [
        applyOptimisticOutlineChapters,
        executeOutlineMutation,
        getReindexedOutlineChapters,
        getSelectionAfterChapterDelete,
        onChangeSelectedPostValuePath,
        rollbackOptimisticOutlineChapters,
    ]);

    const deleteSection = useCallback((chapterId: string, sectionIndex: number): void => {
        const originalChapters = getReindexedOutlineChapters(outlineChaptersRef.current || []);
        const chapterIndex = originalChapters.findIndex((chapter) => chapter.id === chapterId);

        if (chapterIndex < 0) {
            return;
        }

        const targetChapter = originalChapters[chapterIndex];
        const removedSection = (targetChapter.sections || [])[sectionIndex];

        if (!removedSection) {
            return;
        }

        const previousSelectedPath = [...latestSelectedPostValuePathRef.current];
        const nextSections = (targetChapter.sections || []).filter((section) => section.id !== removedSection.id)
            .map((section, index) => ({
                ...section,
                index,
            }));
        const nextChapters = [...originalChapters];
        nextChapters[chapterIndex] = {
            ...targetChapter,
            sections: nextSections,
        };
        const reindexedNextChapters = getReindexedOutlineChapters(nextChapters);
        const nextPath = getSelectionAfterSectionDelete(
            reindexedNextChapters,
            chapterIndex,
            sectionIndex,
            previousSelectedPath,
        );

        applyOptimisticOutlineChapters(reindexedNextChapters);
        onChangeSelectedPostValuePath({
            path: nextPath,
            // Force a clean editor remount when the selected scope is deleted.
            clearEditorSelection: true,
        });

        executeOutlineMutation({
            action: POST_OUTLINE_MUTATION_TYPE.deleteSection,
            chapterId,
            sectionId: removedSection.id,
        }).catch(() => {
            rollbackOptimisticOutlineChapters(originalChapters);
            onChangeSelectedPostValuePath({
                path: previousSelectedPath,
            });
        });
    }, [
        applyOptimisticOutlineChapters,
        executeOutlineMutation,
        getReindexedOutlineChapters,
        getSelectionAfterSectionDelete,
        onChangeSelectedPostValuePath,
        rollbackOptimisticOutlineChapters,
    ]);

    /**
     * Manages response to chapter button hovering over
     * @param e mouse event
     */
    const onAddChapterButtonEnter = (e: React.MouseEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.addChapterButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    /**
     * Manages response to chapter button no longer hovering over
     * @param e mouse event
     */
    const onAddChapterButtonLeave = (e: React.MouseEvent): void => {
        onCursorLeave(e);
    };

    // ===== Side Effects =====

    /**
     * Manages hiding contents table on small viewports
     */
    useEffect(() => {
        if (
            viewportDimensions.width < MEDIA_QUERY_SIZE.large.min
            && visible
        ) {
            setContentsTableVisible(false);
        }
    }, [
        viewportDimensions.width,
    ]);

    useEffect(() => {
        const nextChapters = getReindexedOutlineChapters(post?.chapters || []);
        const nextOrderKey = getOutlineOrderKey(nextChapters);
        const optimisticOrderKey = optimisticOutlineOrderKeyRef.current;

        if (optimisticOrderKey) {
            if (nextOrderKey === optimisticOrderKey) {
                optimisticOutlineOrderKeyRef.current = null;
            }
            return;
        }

        if (nextOrderKey !== outlineChapterOrderKey) {
            outlineChaptersRef.current = nextChapters;
            setOutlineChapters(nextChapters);
        }
    }, [
        post?.chapters,
        getOutlineOrderKey,
        getReindexedOutlineChapters,
        outlineChapterOrderKey,
    ]);

    /**
     * Manages hiding contents table when no chapters
     */
    useEffect(() => {
        if (
            !write
            && !!post
            && !!contentsTable
            && contentsTable.length === 0
            && visible
            && pageLoadRendersCompleted
            && !refreshPost
        ) {
            setContentsTableVisible(false);
        }
    }, [
        write,
        post,
        contentsTable,
        visible,
        pageLoadRendersCompleted,
        refreshPost,
    ]);

    /**
     * When chapters or sections are reordered, contentsTable is refreshed
     * and this effect completes the process by loading the first chapter/section
     * so that the selected chapter/section and presented post value remain the same
     */
    useEffect(() => {
        if (updatingContentsTable) {
            if (
                pendingOutlineRefreshActionRef.current !== POST_OUTLINE_MUTATION_TYPE.moveChapter
                && pendingOutlineRefreshActionRef.current !== POST_OUTLINE_MUTATION_TYPE.moveSection
                && pendingOutlineRefreshActionRef.current !== POST_OUTLINE_MUTATION_TYPE.createChapter
                && pendingOutlineRefreshActionRef.current !== POST_OUTLINE_MUTATION_TYPE.createSection
                && pendingOutlineRefreshActionRef.current !== POST_OUTLINE_MUTATION_TYPE.deleteChapter
                && pendingOutlineRefreshActionRef.current !== POST_OUTLINE_MUTATION_TYPE.deleteSection
            ) {
                if (
                    contentsTable
                    && contentsTable[0].sections
                ) {
                    onChangeSelectedPostValuePath({
                        path: [0, 0],
                    });
                } else if (contentsTable) {
                    onChangeSelectedPostValuePath({
                        path: [0],
                    });
                }
            }
            pendingOutlineRefreshActionRef.current = null;
            setUpdatingContentsTable(false);
        }
    }, [contentsTable, onChangeSelectedPostValuePath, updatingContentsTable]);

    // ===== Memoization =====

    const postAccessMap: Map<string, IPostAccessItem> = useMemo(() => (
        userPostAccess || new Map()
    ), [userPostAccess]);

    const postAccessItem: IPostAccessItem | null = useMemo(() => {
        if (post && postAccessMap != null) {
            return postAccessMap.get(post.id) || null;
        }

        return null;
    }, [
        post,
        postAccessMap,
    ]);

    const limitedAccessItems = useMemo(() => {
        if (
            postAccessItem
            && postAccessItem.limitedAccessItems
        ) {
            return new Map(parseSpecialNumbers<Map<string, number>>(postAccessItem.limitedAccessItems)) as Map<string, number>;
        }
        return null;
    }, [postAccessItem]);

    const hasChapterAccess = ({
        chapterIndex,
    }: {
        chapterIndex: number,
    }): boolean => hasReaderOutlineChapterAccess({
        post,
        chapterIndex,
        postAccessItem,
        limitedAccessItems,
        userIsAdmin,
        nowTimestamp: Date.now(),
    });

    const hasSectionAccess = ({
        chapterIndex,
        sectionIndex,
    }: {
        chapterIndex: number,
        sectionIndex: number,
    }): boolean => hasReaderOutlineSectionAccess({
        post,
        chapterIndex,
        sectionIndex,
        postAccessItem,
        limitedAccessItems,
        userIsAdmin,
        nowTimestamp: Date.now(),
    });

    /**
     * Allows native browser link actions while keeping regular left-clicks in
     * the reader's stateful navigation flow.
     */
    const onReadOnlyContentsTableLinkClick = (
        e: React.MouseEvent<HTMLAnchorElement>,
        onActivate: (activationEvent: React.MouseEvent<HTMLAnchorElement>) => void,
    ): void => {
        if (
            e.button !== 0
            || e.metaKey
            || e.ctrlKey
            || e.shiftKey
            || e.altKey
        ) {
            return;
        }

        e.preventDefault();
        onActivate(e);
    };

    const contentsTableToggleButtonBackgroundColor = useMemo(() => {
        if (visible || isContentsTableToggleButtonMouseOver) {
            return themeObj.verascopeColor.purple400;
        }

        return themeObj.color.cream100;
    }, [
        contentsTable,
        isContentsTableToggleButtonMouseOver,
    ]);
    // Split-view visual modes for read-only table styling.
    const steeringReadOnly = !write && isSteering && !hasError;
    const warningReadOnly = !write && hasError;

    // ===== Renderers =====

    /**
     * Renderer for Contents Table Content
     */
    const Contents = useMemo(() => {
        if (
            !write
            && contentsTable
            && currentZoomLevel
        ) {
            return (
                <ContentsTableChaptersContainer>
                    {currentZoomLevel
                    && currentZoomLevel.level >= ZOOM_LEVEL.four
                    && contentsTable.map((chapter, chapterIndex) => {
                        const selectable = chapter.sections && chapter.sections.length > 0;
                        const chapterSelected = selectedPostValuePath.length > 0
                            && selectedPostValuePath[0] === chapterIndex;
                        const chapterPath = [chapterIndex];
                        const chapterHref = buildReaderContentsTableItemHref({
                            chapterId: chapter.id,
                        });
                        const heightExpanded = chapter.sections ? chapter.sections.length * CHAPTER_SECTION_HEIGHT + CHAPTER_HEIGHT_BUFFER : 0;
                        let onActivate = (e: React.MouseEvent | React.TouchEvent): void => {
                            if (shouldIgnoreContentsTableClick()) {
                                e.preventDefault();
                                e.stopPropagation();
                                return;
                            }

                            onChangeSelectedPostValuePath({
                                path: chapterPath,
                                e,
                                expandItems: contentsTable[chapterPath[0]].sections
                                    && contentsTable[chapterPath[0]].sections!.length > 0
                                    && currentZoomLevel
                                    && currentZoomLevel.level === ZOOM_LEVEL.five
                                    && !contentsTable[chapterPath[0]].expanded
                                    ? [{
                                        chapterIndex,
                                        expand: true,
                                    }]
                                    : [],
                            });
                            if (
                                viewportDimensions.width <= MEDIA_QUERY_SIZE.extraSmall.max
                                && visible
                            ) {
                                onToggleContentsTable(e);
                            }
                        };
                        if (selectable && !write) {
                            onActivate = (e: React.MouseEvent | React.TouchEvent): void => {
                                if (shouldIgnoreContentsTableClick()) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                }

                                onSetExpandContentsTableChapter(
                                    [{
                                        chapterIndex,
                                        expand: !contentsTable[chapterPath[0]].expanded,
                                    }],
                                    e,
                                );
                            };
                        }
                        return (
                            <Transition
                                key={chapter.id}
                                in={visible}
                                timeout={{
                                    enter: Math.min(
                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                        (chapterIndex * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION
                                        + CONTENTS_TABLE_TRANSITION_DURATION,
                                    ),
                                    exit: Math.min(
                                        FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ((contentsTable.length - Math.max(chapterIndex - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION,
                                    ),
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(chapterState) => (
                                    <ContentsTableChapter
                                        style={{
                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                direction: 'right',
                                                duration: CONTENTS_TABLE_CHAPTER_ENTER_DURATION,
                                                offset: 15,
                                            }),
                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                direction: 'right',
                                                offset: 15,
                                                numItems: contentsTable.length,
                                                index: chapterIndex,
                                            })[chapterState],
                                        }}
                                    >
                                        <ContentsTableChapterHead
                                            {...(currentZoomLevel && currentZoomLevel.level >= ZOOM_LEVEL.four
                                                ? {
                                                    className: HOVER_TARGET_CLASSNAME,
                                                    customCursorActive,
                                                    onMouseEnter: onContentsTableButtonMouseEnter,
                                                    onMouseLeave: onContentsTableButtonMouseLeave,
                                                    onClick: (e: React.MouseEvent<HTMLAnchorElement>): void => {
                                                        onReadOnlyContentsTableLinkClick(e, onActivate);
                                                    },
                                                }
                                                : {})}
                                            href={chapterHref}
                                            isMobile={isMobile()}
                                            hoverable={currentZoomLevel && currentZoomLevel.level >= ZOOM_LEVEL.four}
                                            selected={chapterSelected}
                                            steeringReadOnly={steeringReadOnly}
                                            warningReadOnly={warningReadOnly}
                                            padding={CHAPTER_HEAD_PADDING}
                                            transitionDuration={CONTENTS_TABLE_CHAPTER_TRANSITION_DURATION}
                                        >
                                            <ContentsTableChapterTitleContainer>
                                                <ContentsTableChapterTitle
                                                    selected={chapterSelected}
                                                    steeringReadOnly={steeringReadOnly}
                                                    warningReadOnly={warningReadOnly}
                                                    fontMultiplier={CHAPTER_HEAD_TITLE_FONT_MULTIPLIER}
                                                    lineHeight={DEFAULT_LINE_HEIGHT}
                                                    transitionDuration={CONTENTS_TABLE_CHAPTER_TRANSITION_DURATION}
                                                >
                                                    {`${chapterIndex + 1}. ${chapter.title}`}
                                                    {post
                                                    && !hasChapterAccess({ chapterIndex })
                                                    && (
                                                        <ContentsTableLockIcon
                                                            selected={chapterSelected}
                                                            steeringReadOnly={steeringReadOnly}
                                                            warningReadOnly={warningReadOnly}
                                                        >
                                                            <ReactSVG
                                                                src={LockIcon}
                                                            />
                                                        </ContentsTableLockIcon>
                                                    )}
                                                    {post
                                                    && hasChapterAccess({ chapterIndex })
                                                    && (
                                                        <ContentsTableLockIcon
                                                            selected={chapterSelected}
                                                            steeringReadOnly={steeringReadOnly}
                                                            warningReadOnly={warningReadOnly}
                                                        >
                                                            <ReactSVG
                                                                src={UnlockIcon}
                                                            />
                                                        </ContentsTableLockIcon>
                                                    )}
                                                </ContentsTableChapterTitle>
                                                {selectable
                                                && currentZoomLevel
                                                && currentZoomLevel.level === ZOOM_LEVEL.five
                                                && (
                                                    <ContentsTableAccordionIcon
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        expanded={!!chapter.expanded}
                                                        transitionDuration={CONTENTS_TABLE_CHAPTER_TRANSITION_DURATION}
                                                    >
                                                        <ReactSVG
                                                            src={ChevronIcon}
                                                        />
                                                    </ContentsTableAccordionIcon>
                                                )}
                                            </ContentsTableChapterTitleContainer>
                                            <ContentsTableChapterDescription
                                                selected={chapterSelected}
                                                steeringReadOnly={steeringReadOnly}
                                                warningReadOnly={warningReadOnly}
                                                fontMultiplier={CHAPTER_HEAD_DESCRIPTION_FONT_MULTIPLIER}
                                                lineHeight={DEFAULT_LINE_HEIGHT}
                                                transitionDuration={CONTENTS_TABLE_CHAPTER_TRANSITION_DURATION}
                                            >
                                                {chapter.description}
                                            </ContentsTableChapterDescription>
                                            {chapter.annotationCount > 0
                                                && visible
                                                && (
                                                    <ContentsTableChapterAnnotationContainer>
                                                        <Tooltip
                                                            text="Annotations"
                                                            side={TOOLTIP_TYPE.left}
                                                        />
                                                        <ContentsTableChapterAnnotationIcon
                                                            selected={chapterSelected}
                                                            steeringReadOnly={steeringReadOnly}
                                                            warningReadOnly={warningReadOnly}
                                                            transitionDuration={CONTENTS_TABLE_CHAPTER_TRANSITION_DURATION}
                                                        >
                                                            <ReactSVG
                                                                src={HighlightIcon}
                                                            />
                                                        </ContentsTableChapterAnnotationIcon>
                                                        <ContentsTableChapterAnnotationText
                                                            selected={chapterSelected}
                                                            steeringReadOnly={steeringReadOnly}
                                                            warningReadOnly={warningReadOnly}
                                                            fontMultiplier={CHAPTER_ANNOTATION_TEXT_FONT_MULTIPLIER}
                                                            transitionDuration={CONTENTS_TABLE_CHAPTER_TRANSITION_DURATION}
                                                        >
                                                            {formatNumber(chapter.annotationCount)}
                                                        </ContentsTableChapterAnnotationText>
                                                    </ContentsTableChapterAnnotationContainer>
                                                )}
                                        </ContentsTableChapterHead>
                                        {currentZoomLevel
                                        && currentZoomLevel.level === ZOOM_LEVEL.five
                                        && (
                                            <ContentsTableChapterBody
                                                visible={!!chapter.expanded}
                                                heightExpanded={heightExpanded}
                                                transitionDuration={CONTENTS_TABLE_CHAPTER_TRANSITION_DURATION}
                                                transitionDelayDuration={(chapter.sections
                                                    ? chapter.sections.length * FADE_IN_STAGGER_OFFSET_DURATION
                                                    : 0)
                                                    + FADE_IN_STAGGER_TRANSITION_DURATION}
                                            >
                                                {chapter.sections && (
                                                    chapter.sections.map((
                                                        section: IContentsChapterSectionItem,
                                                        sectionIndex: number,
                                                    ) => {
                                                        const sectionSelected = selectedPostValuePath.length === 2
                                                            && selectedPostValuePath[0] === chapterIndex
                                                            && selectedPostValuePath[1] === sectionIndex;
                                                        const sectionPath = [chapterIndex, sectionIndex];
                                                        const sectionHref = buildReaderContentsTableItemHref({
                                                            chapterId: chapter.id,
                                                            sectionId: section.id,
                                                        });
                                                        return (
                                                            <Transition
                                                                key={section.id}
                                                                in={chapter.expanded}
                                                                timeout={{
                                                                    enter: Math.min(
                                                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                                                        (sectionIndex * FADE_IN_STAGGER_OFFSET_DURATION),
                                                                    ),
                                                                    exit: (
                                                                        (
                                                                            chapter.sections!.length
                                                                            - Math.max(sectionIndex - 1, 0)
                                                                        )
                                                                        * FADE_IN_STAGGER_OFFSET_DURATION
                                                                    ),
                                                                }}
                                                            >
                                                                {(sectionState) => (
                                                                    <ContentsTableChapterSection
                                                                        className={HOVER_TARGET_CLASSNAME}
                                                                        customCursorActive={customCursorActive}
                                                                        isMobile={isMobile()}
                                                                        steeringReadOnly={steeringReadOnly}
                                                                        warningReadOnly={warningReadOnly}
                                                                        transitionDuration={CONTENTS_TABLE_CHAPTER_SECTION_TRANSITION_DURATION}
                                                                        href={sectionHref}
                                                                        {...getTouchMouseEventHandlers({
                                                                            onMouseEnter: onContentsTableButtonMouseEnter,
                                                                            onMouseLeave: onContentsTableButtonMouseLeave,
                                                                        })}
                                                                        onClick={(e: React.MouseEvent<HTMLAnchorElement>): void => {
                                                                            onReadOnlyContentsTableLinkClick(
                                                                                e,
                                                                                (activationEvent) => {
                                                                                    onChangeSelectedPostValuePath({
                                                                                        path: sectionPath,
                                                                                        e: activationEvent,
                                                                                    });
                                                                                    if (
                                                                                        viewportDimensions.width <= MEDIA_QUERY_SIZE.extraSmall.max
                                                                                        && visible
                                                                                    ) {
                                                                                        onToggleContentsTable(activationEvent);
                                                                                    }
                                                                                },
                                                                            );
                                                                        }}
                                                                        style={{
                                                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                                                direction: 'right',
                                                                                duration: CONTENTS_TABLE_CHAPTER_SECTION_TRANSITION_DURATION,
                                                                                offset: 10,
                                                                            }),
                                                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                                                direction: 'right',
                                                                                offset: 10,
                                                                                numItems: chapter.sections!.length,
                                                                                index: sectionIndex,
                                                                            })[sectionState],
                                                                        }}
                                                                    >
                                                                        <ContentsTableChapterSectionText
                                                                            selected={sectionSelected}
                                                                            steeringReadOnly={steeringReadOnly}
                                                                            warningReadOnly={warningReadOnly}
                                                                            fontMultiplier={CHAPTER_SECTION_FONT_MULTIPLIER}
                                                                            lineHeight={DEFAULT_LINE_HEIGHT}
                                                                            padding={CHAPTER_SECTION_PADDING}
                                                                            transitionDuration={
                                                                                CONTENTS_TABLE_CHAPTER_SECTION_TEXT_TRANSITION_DURATION
                                                                            }
                                                                        >
                                                                            {section.title}
                                                                            {post
                                                                            && !hasSectionAccess({ chapterIndex, sectionIndex })
                                                                            && (
                                                                                <ContentsTableLockIcon
                                                                                    selected={sectionSelected}
                                                                                    steeringReadOnly={steeringReadOnly}
                                                                                    warningReadOnly={warningReadOnly}
                                                                                >
                                                                                    <ReactSVG
                                                                                        src={LockIcon}
                                                                                    />
                                                                                </ContentsTableLockIcon>
                                                                            )}
                                                                            {post
                                                                            && hasSectionAccess({ chapterIndex, sectionIndex })
                                                                            && (
                                                                                <ContentsTableLockIcon
                                                                                    selected={sectionSelected}
                                                                                    steeringReadOnly={steeringReadOnly}
                                                                                    warningReadOnly={warningReadOnly}
                                                                                >
                                                                                    <ReactSVG
                                                                                        src={UnlockIcon}
                                                                                    />
                                                                                </ContentsTableLockIcon>
                                                                            )}
                                                                        </ContentsTableChapterSectionText>
                                                                        <ContentsTableChapterSectionLine
                                                                            selected={sectionSelected}
                                                                            visible={section.annotationCount > 0}
                                                                            steeringReadOnly={steeringReadOnly}
                                                                            warningReadOnly={warningReadOnly}
                                                                        />
                                                                        {section.annotationCount > 0
                                                                        && visible
                                                                        && (
                                                                            <ContentsTableChapterSectionAnnotationContainer>
                                                                                <Tooltip
                                                                                    text="Annotations"
                                                                                    side={TOOLTIP_TYPE.left}
                                                                                />
                                                                                <ContentsTableChapterSectionAnnotationIcon
                                                                                    selected={sectionSelected}
                                                                                    steeringReadOnly={steeringReadOnly}
                                                                                    warningReadOnly={warningReadOnly}
                                                                                    transitionDuration={CONTENTS_TABLE_CHAPTER_TRANSITION_DURATION}
                                                                                >
                                                                                    <ReactSVG
                                                                                        src={HighlightIcon}
                                                                                    />
                                                                                </ContentsTableChapterSectionAnnotationIcon>
                                                                                <ContentsTableChapterSectionAnnotationText
                                                                                    selected={sectionSelected}
                                                                                    steeringReadOnly={steeringReadOnly}
                                                                                    warningReadOnly={warningReadOnly}
                                                                                    fontMultiplier={CHAPTER_ANNOTATION_TEXT_FONT_MULTIPLIER}
                                                                                    transitionDuration={CONTENTS_TABLE_CHAPTER_TRANSITION_DURATION}
                                                                                >
                                                                                    {formatNumber(section.annotationCount)}
                                                                                </ContentsTableChapterSectionAnnotationText>
                                                                            </ContentsTableChapterSectionAnnotationContainer>
                                                                        )}
                                                                    </ContentsTableChapterSection>
                                                                )}
                                                            </Transition>
                                                        );
                                                    })
                                                )}
                                            </ContentsTableChapterBody>
                                        )}
                                    </ContentsTableChapter>
                                )}
                            </Transition>
                        );
                    })}
                </ContentsTableChaptersContainer>
            );
        }

        return (
            <ContentsTableOutlineContainer
                ref={drop}
            >
                {currentZoomLevel
                && currentZoomLevel.level >= ZOOM_LEVEL.four
                && post
                // It's fine to use post.chapters because we expect the full post when editing it.
                && post?.chapters
                && outlineChapters
                && outlineChapters.map((chapter) => (
                    <ReaderOutlineChapter
                        key={chapter.id}
                        customCursorActive={customCursorActive}
                        hasSound={hasSound}
                        postId={post.id}
                        write={write}
                        chapter={chapter}
                        currentSessionId={currentSessionId}
                        currentZoomLevel={currentZoomLevel}
                        // It's fine to use post.chapters because we expect the full post when editing it.
                        chapters={outlineChapters!}
                        annotationCount={annotationCountsByChapterId[chapter.id] || 0}
                        sectionAnnotationCountsById={annotationCountsBySectionId}
                        interactionDisabled={interactionDisabled || updatingContentsTable}
                        previewMoveChapter={previewMoveChapter}
                        commitMoveChapter={commitMoveChapter}
                        resetMoveChapterPreview={resetMoveChapterPreview}
                        previewMoveSection={previewMoveSection}
                        commitMoveSection={commitMoveSection}
                        resetMoveSectionPreview={resetMoveSectionPreview}
                        addSection={addSection}
                        deleteChapter={deleteChapter}
                        deleteSection={deleteSection}
                        updateChapterTitle={updateChapterTitle}
                        flushChapterTitle={flushChapterTitle}
                        updateChapterDescription={updateChapterDescription}
                        flushChapterDescription={flushChapterDescription}
                        updateSectionTitle={updateSectionTitle}
                        flushSectionTitle={flushSectionTitle}
                        setCursorSigns={setCursorSigns}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setSnackbarData={setSnackbarData}
                        selectedPostValuePath={selectedPostValuePath}
                        onChangeSelectedPostValuePath={onChangeSelectedPostValuePath}
                    />
                ))}
                {currentZoomLevel
                && currentZoomLevel.level >= ZOOM_LEVEL.four
                && !interactionDisabled
                && (
                    <AddChapterButton
                        className={HOVER_TARGET_CLASSNAME}
                        disabled={interactionDisabled || updatingContentsTable}
                        transitionDuration={ADD_CHAPTER_BUTTON_TRANSITION_DURATION}
                        {...getPressEventHandlers({
                            onTouchStart: !interactionDisabled && !updatingContentsTable ? onAddChapterButtonMouseDown : undefined,
                            onMouseDown: !interactionDisabled && !updatingContentsTable ? onAddChapterButtonMouseDown : undefined,
                        })}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onAddChapterButtonEnter,
                            onMouseLeave: onAddChapterButtonLeave,
                        })}
                    >
                        <Tooltip
                            text="Add Chapter"
                            side={TOOLTIP_TYPE.right}
                        />
                        <AddChapterButtonIcon>
                            <ReactSVG
                                src={PlusIcon}
                            />
                        </AddChapterButtonIcon>
                    </AddChapterButton>
                )}
            </ContentsTableOutlineContainer>
        );
    }, [
        write,
        post,
        outlineChapters,
        contentsTable,
        interactionDisabled,
        updatingContentsTable,
        selectedPostValuePath,
        visible,
        currentZoomLevel,
        steeringReadOnly,
        warningReadOnly,
        customCursorActive,
        contentsTableToggleButtonBackgroundColor,
        viewportDimensions.width,
    ]);

    return (
        <>
            <Transition
                in={
                    !hide
                    && !!currentZoomLevel
                    && currentZoomLevel.level > ZOOM_LEVEL.three
                    && (
                        write
                        || (!write && !!contentsTable && contentsTable.length > 0)
                    )
                }
                timeout={{
                    enter: POST_TITLE_ENTER_DURATION
                        + POST_SUBTITLE_ENTER_DURATION
                        + POST_THUMBNAIL_ENTER_DURATION
                        + POST_METADATA_ENTER_DURATION,
                    exit: 0,
                }}
                appear
                mountOnEnter
                unmountOnExit
            >
                {(tableState) => (
                    <ContentsTable
                        ref={contentsTableRef}
                        contentsTableVisible={visible}
                        widthExpanded={CONTENTS_TABLE_WIDTH_EXPANDED}
                        widthContracted={CONTENTS_TABLE_WIDTH_CONTRACTED}
                        margin={CONTENTS_TABLE_MARGIN}
                        heightExpanded={viewportDimensions.height - POST_BANNER_HEIGHT}
                        topExpanded={CONTENTS_TABLE_TOP_EXPANDED}
                        transitionDuration={CONTENTS_TABLE_ENTER_TRANSITION_DURATION}
                        showWarning={hasError}
                        showDetailMinimumWarning={hitDetailMinimum}
                        showSteering={steeringReadOnly}
                        notifyUserToSignUpAfterAnnotationCreationAttempt={notifyUserToSignUpAfterAnnotationCreationAttempt}
                        onScroll={onContentsTableScroll}
                        style={{
                            ...FADE_IN_DEFAULT_STYLE({
                                direction: 'right',
                                offset: POST_BANNER_LEFT_CONTAINER_WIDTH + CONTENTS_TABLE_MARGIN_RIGHT,
                                duration: CONTENTS_TABLE_TRANSITION_DURATION,
                                easing: themeObj.motion.eagerEasing,
                            }),
                            ...FADE_IN_TRANSITION_STYLES({
                                direction: 'right',
                                offset: POST_BANNER_LEFT_CONTAINER_WIDTH + CONTENTS_TABLE_MARGIN_RIGHT,
                            })[tableState],
                        }}
                    >
                        {!write && (
                            <ContentsTableProgressBarContainer
                                ref={contentsTableProgressBarContainerRef}
                            >
                                <ContentsTableProgressBar
                                    ref={contentsTableProgressBarRef}
                                    contentsTableVisible={visible}
                                    transitionDuration={CONTENTS_TABLE_PROGRESS_BAR_TRANSITION_DURATION}
                                />
                            </ContentsTableProgressBarContainer>
                        )}
                        {visible
                        && (
                            <ContentsTableTitle
                                warningReadOnly={warningReadOnly}
                                steeringReadOnly={steeringReadOnly}
                                fontMultiplier={CONTENTS_TABLE_TITLE_FONT_MULTIPLIER}
                                lineHeight={DEFAULT_LINE_HEIGHT}
                                paddingTop={CONTENTS_TABLE_TITLE_PADDING_TOP}
                            >
                                {write ? 'Outline' : 'Contents'}
                            </ContentsTableTitle>
                        )}
                        {Contents}
                    </ContentsTable>
                )}
            </Transition>
            <Transition
                in={
                    !hide
                    && !!currentZoomLevel
                    && currentZoomLevel.level > ZOOM_LEVEL.three
                    && (
                        write
                        || (!write && !!contentsTable && contentsTable.length > 0)
                    )
                }
                timeout={{
                    enter: POST_TITLE_ENTER_DURATION
                        + POST_SUBTITLE_ENTER_DURATION
                        + POST_THUMBNAIL_ENTER_DURATION
                        + POST_METADATA_ENTER_DURATION,
                    exit: 0,
                }}
                appear
                mountOnEnter
                unmountOnExit
            >
                {(state) => (
                    <ContentsTableToggleButtonContainer
                        ref={contentsTableToggleButtonContainerRef}
                        isVisible={
                            !hide
                            && !!currentZoomLevel
                            && currentZoomLevel.level > ZOOM_LEVEL.three
                            && (write || (!write && !!contentsTable && contentsTable.length > 0)
                            )
                        }
                        topExpanded={viewportDimensions.width > MEDIA_QUERY_SIZE.small.min
                            ? POST_BANNER_HEIGHT + CONTENTS_TABLE_MARGIN
                            : POST_BANNER_HEIGHT + CONTENTS_TABLE_MARGIN + CONTENTS_TABLE_TOGGLE_BUTTON_CONTAINER_Y_OFFSET_SMALL_VIEWPORT}
                        leftExpanded={POST_BANNER_LEFT_CONTAINER_WIDTH
                            - CONTENTS_TABLE_MARGIN_RIGHT
                            + CONTENTS_TABLE_MARGIN
                            + CONTENTS_TABLE_TOGGLE_BUTTON_MARGIN}
                        leftContracted={
                            CONTENTS_TABLE_MARGIN
                            + CONTENTS_TABLE_WIDTH_CONTRACTED
                            + CONTENTS_TABLE_TOGGLE_BUTTON_MARGIN
                        }
                        height={CONTENTS_TABLE_TOGGLE_BUTTON_LENGTH}
                        tableVisible={visible}
                        notifyUserToSignUpAfterAnnotationCreationAttempt={notifyUserToSignUpAfterAnnotationCreationAttempt}
                        transitionDuration={CONTENTS_TABLE_ENTER_TRANSITION_DURATION}
                        style={{
                            ...FADE_IN_DEFAULT_STYLE({
                                direction: 'right',
                                offset: POST_BANNER_LEFT_CONTAINER_WIDTH + CONTENTS_TABLE_MARGIN_RIGHT,
                                duration: CONTENTS_TABLE_TRANSITION_DURATION,
                                easing: themeObj.motion.eagerEasing,
                            }),
                            ...FADE_IN_TRANSITION_STYLES({
                                direction: 'right',
                                offset: POST_BANNER_LEFT_CONTAINER_WIDTH + CONTENTS_TABLE_MARGIN_RIGHT,
                            })[state],
                        }}
                    >
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            background={contentsTableToggleButtonBackgroundColor}
                            height={CONTENTS_TABLE_TOGGLE_BUTTON_LENGTH}
                            width={CONTENTS_TABLE_TOGGLE_BUTTON_LENGTH}
                            icon={ChevronIcon}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onContentsTableToggleButtonMouseEnter,
                                onMouseLeave: onContentsTableToggleButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onMouseDown:
                                (e) => {
                                    e.stopPropagation();
                                    onToggleContentsTable(e);
                                },
                            })}
                        />
                    </ContentsTableToggleButtonContainer>
                )}
            </Transition>
        </>
    );
}

export default ReaderContentsTableOutliner;
